import React, { useEffect, useContext, useState, useRef } from 'react';
import FadeIn from 'react-fade-in';
import { GlobalHotKeys } from 'react-hotkeys';
import { useObserver } from 'mobx-react-lite';
import { toJS } from 'mobx';

import QuickDrawerHeader from '../../../../_shared/QuickDrawerHeader';
import { quickDrawerFocus, renderQuickDrawerLoading } from '../../../../_shared/QuickDrawer';

import ExamTemplateUpdateStore from '../../../../../../stores/ExamTemplateUpdateStore';
import QuickDrawerStore from '../../../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../../../constants/errorMessages';
import * as fn from '../../../../../../utilities/_functions';
import * as oh from '../../../../../../utilities/operationHelper';

import api from '../../../../../../api';

import './ReorderExamSection.scss';

function ReorderExamSection(props) {
    const isMounted = useRef(true);
    const focusTimer = useRef(null);
    const examTemplate = useContext(ExamTemplateUpdateStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const [ready, setReady] = useState(false);
    const [sections, setSections] = useState([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
        setSections(toJS([...examTemplate.sortedSections]));
        focusTimer.current = setTimeout(() => {
            api.Roles.all()
                .then(({ data }) => {
                    if (isMounted.current) {
                        quickDrawerFocus(props.drawer);
                        setReady(true);
                    }
                })
        }, 100)

        return () => {
            isMounted.current = false;
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        }
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleSubmit = event => {
        event.preventDefault();

        examTemplate.data.sections = sections;
        examTemplate.hasUnsavedChanges = true;

        examTemplate.save()
            .then(() => {
                if (isMounted.current) {
                    if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                        props.onSuccess();
                    }
                }
            })
    }

    const handleSectionOrderChange = (event, { id }, value) => {
        let newSections = [...sections];
        const index = newSections.findIndex(s => s.id === id);

        if (index >= 0) {
            const displayOrder = newSections[index].displayOrder;
            const newDisplayOrder = newSections[index].displayOrder + value;
            const flipIndex = newSections.findIndex(s => s.id !== id && s.displayOrder === newDisplayOrder);

            newSections[index].displayOrder = newDisplayOrder;

            if (flipIndex >= 0) {
                newSections[flipIndex].displayOrder = displayOrder;
            }

            newSections = newSections.sort((a, b) => { return a.displayOrder - b.displayOrder });
            setSections(newSections);
            setHasUnsavedChanges(true);
        }
    }

    return useObserver(() => <>
        <>
            {
                (props.drawer === quickDrawer.drawerOpened) ?
                    <GlobalHotKeys
                        keyMap={{
                            close: ['esc'],
                        }}
                        handlers={{
                            close: event => {
                                handleCancel(event)
                            },
                        }}
                        allowChanges={true}
                    /> : null
            }
        </>
        <form onSubmit={handleSubmit}>
            <fieldset disabled={examTemplate.isSaving}>
                <div className='quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('exam-section', 'reorder')}
                        action='Reorder'
                        category='Exam Tabs'
                        onCancel={handleCancel}
                    />
                    <div className='quick-drawer-body'>
                        {
                            ready ?
                                <FadeIn>
                                    <div className='new-exam-section body-content'>
                                        <section>
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='new-exam-section'><small>Sections</small></label>
                                                        {
                                                            sections.length > 0 ?
                                                                <div className='border-bottom'>
                                                                    {
                                                                        sections.map((s, si) => {
                                                                            return <div
                                                                                key={`sorted_exam_section_${si}`}
                                                                                className='border border-bottom-0 d-flex p-2'
                                                                            >
                                                                                <div className='d-flex align-items-center flex-1 ml-auto'><strong>{s.name}</strong></div>
                                                                                <button
                                                                                    type='button'
                                                                                    className='btn btn-icon btn-xs ml-1'
                                                                                    disabled={(si === 0)}
                                                                                    onClick={(e) => { handleSectionOrderChange(e, s, -1) }}
                                                                                >
                                                                                    <i className={'fal fa-arrow-up ' + (si === 0 ? 'text-gray-500 cursor-not-allowed' : 'text-info')}></i>
                                                                                </button>
                                                                                <button
                                                                                    type='button'
                                                                                    className='btn btn-icon btn-xs ml-1'
                                                                                    disabled={(si === (sections.length - 1))}
                                                                                    onClick={(e) => { handleSectionOrderChange(e, s, 1) }}
                                                                                >
                                                                                    <i className={'fal fa-arrow-down ' + (si === (sections.length - 1) ? 'text-gray-500 cursor-not-allowed' : 'text-info')}></i>
                                                                                </button>
                                                                            </div>
                                                                        })
                                                                    }
                                                                </div> : null
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                    </div>
                                </FadeIn> : renderQuickDrawerLoading()
                        }
                    </div>
                    <div className='quick-drawer-action'>
                        <div className='row'>
                            <div className='col-4'></div>
                            <div className='col-8'>
                                <div className='float-right'>
                                    <button
                                        type='button'
                                        className='btn btn-link btn-cancel mr-2'
                                        onClick={handleCancel}
                                    >Cancel</button>
                                    <button
                                        type='submit'
                                        className='btn btn-success'
                                    >Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
    </>)
}

export default ReorderExamSection;